import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import { takeSnapshotSetter } from "../index";
import { updateNodeSetter } from "./updateNodeSetter";

/**
 * Asset ID fields that should be cleared when unlinking assets from a node.
 * Add new asset types here to ensure they're properly unlinked.
 */
const ASSET_ID_FIELDS = [
  "tableId",
  "projectId",
  "pageId",
  "chatbotId",
  "agentId",
  "zapId",
] as const;

/**
 * Atom family setter that unlinks all assets from a specific node.
 * Clears all asset-related IDs from the node's data.
 *
 * @param nodeId - The ID of the node to unlink assets from
 * @returns A setter atom that clears all asset-related IDs from the node
 */
export const unlinkAssetsAtomFamily = atomFamily((nodeId: string) => {
  const unlinkSetter = atom(null, (_, set) => {
    // Take snapshot before making changes for undo functionality
    set(takeSnapshotSetter);

    // Create an object with all asset ID fields set to undefined
    const clearedAssetData = ASSET_ID_FIELDS.reduce(
      (acc, field) => ({ ...acc, [field]: undefined }),
      {} as Record<string, undefined>,
    );

    set(updateNodeSetter, {
      id: nodeId,
      data: clearedAssetData,
    });
  });

  unlinkSetter.debugLabel = `unlinkAssetsAtomFamily(${nodeId})`;
  return unlinkSetter;
});
